import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import { ReactComponent as ValidIcon } from "../../images/check_marker_green.svg";
import { ReactComponent as InvalidIcon } from "../../images/cross_cricle_red.svg";
import styles from "./OverrideModal.module.css";
import {
    APP_ID,
    APP_KEY,
    BLACK_BOARD_ACCESS_TOKEN,
    BUTTON,
    CLOSE_MODAL, D2L_ACCESS_TOKEN, EVP_API_TOKEN_OVERWRITE_MODAL_DESC, EVP_API_TOKEN_OVERWRITE_MODAL_TITLE, EVP_TEXT,
    INTEGRATION_GUIDE,
    INTEGRATION_TYPES_LOWERCASE,
    MODAL,
    MODAL_CANCEL_TEXT,
    MODAL_CONFIRM_TEXT,
    TEXTBOX
} from "../../utils/constants";
import YuJaButton from "../standardization/YuJaButton";
import { YuJaTextBox } from "../standardization/YuJaTextBox";

// One entry per lmsType. "fields" holds one entry for a single-value override (Moodle/EVP) or two
// for LMS types whose access key is really a pair entered/saved together (Canvas: Client ID/Secret;
// D2L: App ID/Key; Blackboard: App Key/Secret). The modal just iterates "fields" and renders as many
// AccessKeyFields as are listed - no separate single-field/two-field code paths.
const FIELD_CONFIG = {
    [INTEGRATION_TYPES_LOWERCASE.MOODLE]: {
        title: "Add Access Key",
        guide: INTEGRATION_GUIDE.MOODLE.accessKeyGuide,
        fields: [
            {key: "field1", label: "Access Token", hint: "The access token found in Moodle", placeholder: "Enter access token", requiredMessage: "Access token is required"},
        ],
    },
    [EVP_TEXT]: {
        title: EVP_API_TOKEN_OVERWRITE_MODAL_TITLE,
        description: EVP_API_TOKEN_OVERWRITE_MODAL_DESC,
        fields: [
            {key: "field1", label: "API Token", hint: "", placeholder: "Enter API token", requiredMessage: "API token is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.CANVAS]: {
        title: "Add Access Key",
        guide: INTEGRATION_GUIDE.CANVAS.accessKeyGuide,
        fields: [
            {key: "field1", label: "Client ID", hint: "The client ID found in Canvas", placeholder: "Enter the Client ID", requiredMessage: "Client ID is required"},
            {key: "field2", label: "Client Secret", hint: "The client secret found in Canvas", placeholder: "Enter the Client Secret", requiredMessage: "Client secret is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.D2L]: {
        title: "Add Access Key",
        guide: INTEGRATION_GUIDE.D2L.accessKeyGuide,
        fields: [
            {key: "field1", label: APP_ID, hint: "The App ID used for D2L API access", placeholder: "Enter the App ID", requiredMessage: "App ID is required"},
            {key: "field2", label: D2L_ACCESS_TOKEN, hint: "Allows Engage to retrieve data from your LMS", placeholder: "Enter the App Key", requiredMessage: "App Key is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.BLACKBOARD]: {
        title: "Add Access Key",
        guide: INTEGRATION_GUIDE.BLACKBOARD.accessKeyGuide,
        fields: [
            {key: "field1", label: APP_KEY, hint: "The App Key used for Blackboard API access", placeholder: "Enter the App Key", requiredMessage: "App Key is required"},
            {key: "field2", label: BLACK_BOARD_ACCESS_TOKEN, hint: "Allows Engage to retrieve data from your LMS", placeholder: "Enter the App Secret", requiredMessage: "App Secret is required"},
        ],
    },
};

function AccessKeyField({label, description, requiredMessage, isValid, ...textBoxProps}) {
    return (
        <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>{label}</div>
            {description && <div className={styles.fieldHint}>{description}</div>}
            <YuJaTextBox
                label={label}
                containerStyle={{marginBottom: 0}}
                {...textBoxProps}
            />
            <div className={styles.validationRow}>
                {isValid ? <ValidIcon /> : <InvalidIcon />}
                <span>{requiredMessage}</span>
            </div>
        </div>
    );
}

export default function OverrideModal({config, show, AlertType=MODAL_CONFIRM_TEXT, handleclose=null, lmsType=INTEGRATION_TYPES_LOWERCASE.MOODLE}) {

    const entry = FIELD_CONFIG[lmsType] || FIELD_CONFIG[INTEGRATION_TYPES_LOWERCASE.MOODLE];
    const isMultiField = entry.fields.length > 1;
    const initialValues = config.initialValues || {};

    const [values, setValues] = useState(() =>
        Object.fromEntries(entry.fields.map(f => [f.key, initialValues[f.key] || ""]))
    );

    useEffect(() => {
        if (show) {
            setValues(Object.fromEntries(entry.fields.map(f => [f.key, initialValues[f.key] || ""])));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, lmsType]);

    const setFieldValue = (key, value) => setValues(prev => ({...prev, [key]: value}));
    const isFieldValid = (key) => (values[key] || "").trim() !== "";
    const submitDisabled = entry.fields.some(f => !isFieldValid(f.key));

    return (
        <Modal
            id="form-modal"
            show={show}
            onHide={!handleclose ? config.close : handleclose}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={true}
            dialogClassName={isMultiField ? styles.dialog : undefined}
        >
            <ModalClose tabIndex={0} aria-label={CLOSE_MODAL} role={BUTTON} onClick={handleclose ? handleclose : config.close} className="modal-close" />
            <Modal.Header
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Modal.Title tabIndex={0} role={TEXTBOX} aria-label={config.title + MODAL} className="modal-title" id="contained-modal-title-vcenter">
                    {entry.title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={styles.container}>
                    <div className={styles.description}>
                        {entry.guide
                            ? <>
                                {entry.guide.description}{" "}
                                <a
                                    href={entry.guide.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}
                                >
                                    {entry.guide.linkText}
                                </a>
                                {"."}
                            </>
                            : entry.description
                        }
                    </div>
                    {entry.fields.map(field => (
                        <AccessKeyField
                            key={field.key}
                            label={field.label}
                            description={field.hint}
                            requiredMessage={field.requiredMessage}
                            isValid={isFieldValid(field.key)}
                            placeholder={field.placeholder}
                            name={field.key}
                            value={values[field.key] || ""}
                            onChange={(e) => setFieldValue(field.key, e.target.value)}
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <YuJaButton type="secondary" onClick={config.close}>
                    {config.cancelText || MODAL_CANCEL_TEXT}
                </YuJaButton>
                <YuJaButton
                    onClick={(e) => {
                        e.preventDefault();
                        config.submit(...entry.fields.map(f => values[f.key] || ""));
                    }}
                    type={AlertType=== MODAL_CONFIRM_TEXT ? "primary" : "delete"}
                    disabled = {submitDisabled}
                >
                    {config.okText || "Override"}
                </YuJaButton>
            </Modal.Footer>
        </Modal>
    );
}
